import React, { useState } from 'react';

import FormControl, { FormControlClassKey } from '@material-ui/core/FormControl';
import { FormHelperTextClassKey } from '@material-ui/core/FormHelperText';
import { InputClassKey, InputProps } from '@material-ui/core/Input';
import { InputLabelClassKey } from '@material-ui/core/InputLabel';
import { OutlinedInputClassKey } from '@material-ui/core/OutlinedInput';
import MaskedInput from 'react-input-mask';

import { CharAmount } from './Components/CharAmount';
import { ComponentInput } from './Components/ComponentInput';
import { EndAdornment } from './Components/EndAdornment';
import { HelperText } from './Components/HelperText';
import { Label } from './Components/Label';
import { TextAreaAdornment } from './Components/TextAreaAdornment';

interface IInputProps extends InputProps {
  classes?: Partial<Record<InputClassKey | OutlinedInputClassKey | 'charAmount', string>>;
  formControlClasses?: Partial<Record<FormControlClassKey, string>>;
  labelClasses?: Partial<Record<InputLabelClassKey, string>>;
  formHelperTextClasses?: Partial<Record<FormHelperTextClassKey, string>>;
  errorMessage?: string;
  hasError?: boolean;
  label?: string;
  lazy?: boolean;
  mask?: string;
  noIcon?: boolean;
  outlined?: boolean;
  showCharAmount?: boolean;
  type: 'text' | 'password' | 'email' | 'search' | 'number' | 'handicap';
  validated?: boolean;
}

export const Input = (props: IInputProps) => {
  const [active, setActive] = useState(false);
  const [validate, setValidate] = useState(!!props.validated);
  const [lazyValue, setValue] = useState(props.value);
  const [handicapNegative, setNegative] = useState(Number(props.value) < 0);

  const {
    id = `id-${Math.round(Math.random() * 1000000)}`,
    classes,
    formControlClasses,
    formHelperTextClasses,
    labelClasses,
    errorMessage,
    hasError,
    label,
    lazy,
    mask,
    noIcon,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    outlined,
    showCharAmount,
    type,
    validated,
    value,
    ...commonProps
  } = props;

  const onBlur = (event: any): void => {
    if (handleBlur) {
      handleBlur(event);
    }
    setActive(false);
    setValidate(true);
    if (lazy && handleChange) {
      handleChange(event);
    }
  };

  const onFocus = (event: any): void => {
    if (handleFocus) {
      handleFocus(event);
    }
    setActive(true);
    setValidate(true);
  };

  const onChange = (event: any): void => {
    if (!lazy && handleChange) {
      if (type === 'handicap') {
        const handicapRegex = /^(\+?)(\d+(.?\d?\d?))?$/;
        const handicapMatch = event.target.value.match(handicapRegex);

        if (handicapMatch) {
          const checkedValue =
            event.target.value === '+'
              ? event.target.value
              : handicapMatch[2]
              ? `${handicapMatch[1] ? '-' : ''}${handicapMatch[2]}`
              : '';

          handleChange({ ...event, target: { ...event.target, value: checkedValue } });
        }
      } else {
        handleChange(event);
      }
    } else {
      if (lazyValue === '' && type === 'handicap') {
        const checkedValue = Number(event.target.value) * (1 - 2 * Number(handicapNegative));
        setValue(checkedValue);
        setNegative(checkedValue < 0);
      } else {
        setValue(event.target.value);
        if (type === 'handicap') {
          setNegative(
            Number(event.target.value) < 0 || (Number(lazyValue) < 0 && event.target.value === '')
          );
        }
      }
    }
  };

  const onKeyPress = (event: any) => {
    const code = event.keyCode || event.which;
    const char = String.fromCharCode(code);
    if (type === 'handicap' && char === '+' && !handicapNegative) {
      setNegative(true);
    }
  };

  const onKeyDown = (event: any) => {
    const code = event.keyCode || event.which;
    if (lazyValue === '' && (code === 8 || code === 46)) {
      setNegative(false);
    }
  };

  const outlinedValues = outlined
    ? {
        error: outlined ? (hasError ? true : false) : null,
        labelWidth: 0,
        variant: outlined ? 'outlined' : null
      }
    : null;

  const variantCSSClasses = `${active ? 'active' : ''}`;

  const adornment = noIcon ? null : commonProps.multiline ? (
    <TextAreaAdornment />
  ) : (
    <EndAdornment error={!!validate && !!hasError} type={type} />
  );

  const symbolAdornment =
    props.label === 'DISCOUNT' && props.value === '' ? undefined : commonProps.startAdornment;

  return (
    <FormControl
      classes={{
        ...formControlClasses,
        root: `input-form-control override ${type} ${variantCSSClasses} ${
          props.disabled ? 'disabled' : ''
        } ${formControlClasses ? formControlClasses.root || '' : ''}`
      }}
      error={validate && hasError}
      fullWidth={commonProps.fullWidth}
    >
      {!outlined && label && <Label id={id} classes={labelClasses} label={label} />}
      {mask ? (
        <MaskedInput
          mask={mask}
          {...commonProps}
          maskChar={(lazy ? lazyValue : value) && type !== 'password' ? '_' : ''}
        >
          {(inputProps: any) => (
            <ComponentInput
              id={id}
              {...inputProps}
              type={type === 'handicap' ? 'text' : type}
              {...outlinedValues}
              autoComplete="new-password"
              onBlur={onBlur}
              onChange={onChange}
              onFocus={onFocus}
              outlined={outlined}
              value={lazy ? lazyValue : value}
              inputProps={props.inputProps}
            />
          )}
        </MaskedInput>
      ) : (
        <ComponentInput
          classes={classes}
          id={id}
          {...commonProps}
          startAdornment={symbolAdornment}
          type={type === 'handicap' ? 'text' : type}
          endAdornment={adornment}
          {...outlinedValues}
          autoComplete="new-password"
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          outlined={outlined}
          value={lazy ? lazyValue : value}
        />
      )}

      {showCharAmount && (
        <CharAmount value={value} {...commonProps} className={classes ? classes.charAmount : ''} />
      )}

      {type !== 'search' && (
        <HelperText
          classes={formHelperTextClasses}
          visible={!!validate && !!hasError}
          message={errorMessage}
        />
      )}
    </FormControl>
  );
};
